/**
 * Fechamento da venda — desconto, formas de pagamento, troco e o botão.
 *
 * É a metade direita do PDV abaixo do carrinho. Três decisões moram aqui:
 *
 * 1. **O front soma para MOSTRAR.** O total desenhado é `subtotal − desconto`
 *    em aritmética inteira, e serve para o operador dizer o valor em voz alta.
 *    Quem decide o total é o servidor (INV-08): se a resposta divergir do que
 *    esta tela mostrou, vale a resposta.
 * 2. **Σ pagamentos tem que fechar EXATAMENTE com o total** (contrato do fluxo
 *    de venda, regra 3). Por isso a tela não deixa digitar os dois valores da
 *    divisão: digita-se o da primeira forma, e a segunda recebe o resto por
 *    subtração. Dois campos livres é a forma mais rápida de produzir um 422 de
 *    "faltam R$ 0,01".
 * 3. **Impedir vale mais que avisar.** Crediário sem cliente identificado fica
 *    desabilitado com a razão escrita ao lado, em vez de falhar depois do
 *    clique. Isso é conveniência de UX, não autorização — quem decide é o
 *    servidor (CONSTITUICAO.md §4.1), e é ele que recusa o crediário sem
 *    limite.
 * 4. **A segunda forma vive numa GAVETA, não empilhada na coluna.** A barra
 *    lateral do PDV tem 400px e já divide espaço com cliente e carrinho — a
 *    segunda forma completa (seletor + parcelas/troco) empilhada ali embaixo
 *    é o que fazia a coluna inteira rolar embaixo do botão "Finalizar venda".
 *    Só o resumo fica na coluna; a edição abre numa gaveta, como qualquer
 *    tarefa focada que não devia disputar espaço com o carrinho.
 *
 * Dinheiro é inteiro em centavos do começo ao fim (CONSTITUICAO.md §1).
 */

import { useId, useState } from 'react'

import { MensagemDeErro } from '../../componentes/Campos'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta, RodapeDaGaveta } from '../../componentes/Gaveta'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import type { Crediario } from '../../plataforma/clientes'
import { analisar, formatar, formatarMoeda, type Centavos } from '../../plataforma/dinheiro'
import {
  descricaoDaForma,
  interpretarDesconto,
  valorDaParcela,
  FORMAS,
  LIMITE_PARCELAS,
  PARCELAS_OFERECIDAS,
  ratear,
  type Forma,
  type PagamentoPedido,
} from '../../plataforma/vendas'

/** O que o fechamento entrega para a tela montar o corpo de POST /vendas. */
export type DadosFechamento = {
  descontoCentavos: Centavos
  pagamentos: PagamentoPedido[]
}

/**
 * O suficiente do cliente para o `Fechamento` decidir crediário — nome (para
 * a frase de recusa) e o bloco de crediário em si.
 *
 * Estrutural de propósito, e não `LinhaCliente` direto: o PDV passa uma
 * `LinhaCliente` (da busca do balcão), e a tela de faturar condicional passa
 * uma `FichaCliente` (de `GET /clientes/{codigo}` — a condicional só guarda
 * `{ codigo, nome }` do cliente, sem crediário). As duas têm exatamente estes
 * dois campos, com o mesmo tipo, e o `Fechamento` não precisa saber qual das
 * duas é.
 */
export type ClienteParaFechamento = {
  nome: string
  crediario: Crediario
}

const ICONE_FORMA: Record<Forma, NomeIcone> = {
  dinheiro: 'payments',
  // Sem ícone de PIX no Heroicons: o QR é o que o cliente vê na maquininha.
  pix: 'barcode_scanner',
  debito: 'point_of_sale',
  credito: 'point_of_sale',
  crediario: 'calendario',
}

/**
 * Lê o campo "valor recebido".
 *
 * Vazio é "pagou justo" — o servidor assume `recebido = valor` quando o campo
 * não vem, e um PDV que não perguntou não pode inventar um número.
 * `-1` marca texto inválido, que trava o botão em vez de virar zero silencioso.
 */
function lerRecebido(texto: string): Centavos | undefined {
  if (texto.trim() === '') return undefined
  const valor = analisar(texto)
  if (valor === null || valor < 0) return -1
  return valor
}

export function Fechamento({
  totalItens,
  subtotalCentavos,
  cliente,
  podeVender,
  enviando,
  erroDesconto,
  erroPagamento,
  aoFinalizar,
  aoAlterar,
  rotuloAcao = 'Finalizar venda',
  rotuloAcaoOcupado = 'Finalizando…',
}: {
  totalItens: number
  subtotalCentavos: Centavos
  cliente: ClienteParaFechamento | null
  /** Conveniência de UX: o porteiro decide de novo no servidor. */
  podeVender: boolean
  enviando: boolean
  /** 422 do servidor no campo `desconto_centavos`. */
  erroDesconto: string
  /** 422 do servidor em `pagamentos`, `pagamento_N` ou `cliente`. */
  erroPagamento: string
  aoFinalizar: (dados: DadosFechamento) => void
  /** Chamado a cada mexida, para a tela apagar o erro da tentativa anterior. */
  aoAlterar: () => void
  /**
   * Texto do botão final. "Finalizar venda" no PDV; a tela de faturar
   * condicional passa "Faturar condicional" — o corpo enviado é o mesmo
   * `POST /vendas`, só o rótulo muda pelo contexto de quem clica.
   */
  rotuloAcao?: string
  rotuloAcaoOcupado?: string
}) {
  const [descontoTexto, setDescontoTexto] = useState('')
  const [dividido, setDividido] = useState(false)
  // A gaveta abre ao dividir e ao clicar "Editar" no resumo; fechar não
  // desfaz a divisão — só tira a edição detalhada da frente.
  const [mostrarDivisao, setMostrarDivisao] = useState(false)
  const [valorPrimeiroTexto, setValorPrimeiroTexto] = useState('')

  const [formaPrimeira, setFormaPrimeira] = useState<Forma>('dinheiro')
  const [parcelasPrimeira, setParcelasPrimeira] = useState(1)
  const [recebidoPrimeiroTexto, setRecebidoPrimeiroTexto] = useState('')

  const [formaSegunda, setFormaSegunda] = useState<Forma>('pix')
  const [parcelasSegunda, setParcelasSegunda] = useState(1)
  const [recebidoSegundoTexto, setRecebidoSegundoTexto] = useState('')

  const idRazaoCrediario = useId()

  const mexeu = () => aoAlterar()

  /* ------------------------------------------------------------- dinheiro */

  const desconto = interpretarDesconto(descontoTexto, subtotalCentavos)
  const totalCentavos = Math.max(0, subtotalCentavos - desconto.centavos)

  // Só o valor da PRIMEIRA forma é digitado; o da segunda é o resto. É o que
  // garante Σ pagamentos ≡ total sem depender da conta de quem digita.
  const valorPrimeiroDigitado = dividido ? analisar(valorPrimeiroTexto) : null
  const valorPrimeiro = dividido ? (valorPrimeiroDigitado ?? 0) : totalCentavos
  const valorSegundo = totalCentavos - valorPrimeiro
  const divisaoInvalida = dividido && (valorPrimeiro <= 0 || valorSegundo <= 0)

  const recebidoPrimeiro = lerRecebido(recebidoPrimeiroTexto)
  const recebidoSegundo = lerRecebido(recebidoSegundoTexto)

  /* -------------------------------------------------------- disponibilidade */

  const crediarioLiberado = Boolean(cliente?.crediario.liberado)
  const razaoCrediario = !cliente
    ? 'Crediário exige cliente identificado — busque o cliente acima.'
    : crediarioLiberado
      ? ''
      : `${cliente.nome} não está com crediário liberado.`

  // No crediário quem manda é o teto do CLIENTE (`parcelas_max` do cadastro);
  // no crédito, o teto do sistema — o mesmo `check` da migração 0012.
  const parcelasMaximas = (forma: Forma) =>
    forma === 'crediario' ? (cliente?.crediario.parcelas_max ?? 1) : LIMITE_PARCELAS

  /* ------------------------------------------------------------ pagamentos */

  const pagamentos: PagamentoPedido[] = [
    montarPagamento(formaPrimeira, valorPrimeiro, parcelasPrimeira, recebidoPrimeiro),
    ...(dividido
      ? [montarPagamento(formaSegunda, valorSegundo, parcelasSegunda, recebidoSegundo)]
      : []),
  ]

  const usaCrediario = pagamentos.some((pagamento) => pagamento.forma === 'crediario')
  const recebidoInvalido = pagamentos.some((pagamento, indice) => {
    if (pagamento.forma !== 'dinheiro') return false
    return (indice === 0 ? recebidoPrimeiro : recebidoSegundo) === -1
  })
  const faltaEmEspecie = pagamentos.some((pagamento, indice) => {
    if (pagamento.forma !== 'dinheiro') return false
    const recebido = indice === 0 ? recebidoPrimeiro : recebidoSegundo
    return recebido !== undefined && recebido >= 0 && recebido < pagamento.valor_centavos
  })

  const razaoBloqueio = !podeVender
    ? 'Você não tem permissão para finalizar vendas.'
    : totalItens === 0
      ? 'Adicione ao menos um item à venda.'
      : // O texto do erro já está colado no campo de desconto; repetir aqui
        // faria a mesma frase aparecer duas vezes na mesma coluna.
        desconto.erro
        ? 'Corrija o desconto para finalizar.'
        : divisaoInvalida
          ? 'Informe quanto entra na primeira forma — o resto vai para a segunda.'
          : usaCrediario && razaoCrediario
            ? razaoCrediario
            : recebidoInvalido
              ? 'O valor recebido não é um número válido.'
              : faltaEmEspecie
                ? 'O valor recebido é menor que o pagamento em dinheiro.'
                : ''

  const pronto = razaoBloqueio === '' && !enviando

  const finalizar = () => {
    if (!pronto) return
    aoFinalizar({ descontoCentavos: desconto.centavos, pagamentos })
  }

  /* --------------------------------------------------------------- render */

  return (
    <div className="shrink-0 overflow-y-auto border-t border-borda p-4">
      <div className="flex justify-between text-[13px] text-texto-suave">
        <span>
          {totalItens} {totalItens === 1 ? 'item' : 'itens'}
        </span>
        <span className="tabular font-semibold text-tinta">{formatarMoeda(subtotalCentavos)}</span>
      </div>

      {/* ------------------------------------------------------- desconto */}
      <div className="mt-3 flex items-center gap-2.5">
        <span className="flex-1 text-[13px] text-texto-suave">Desconto</span>
        <label
          className="composto h-[34px] w-[140px] cursor-text"
          aria-invalid={desconto.erro || erroDesconto ? true : undefined}
        >
          <Icone nome="percent" tamanho={16} className="text-texto-fraco" />
          <input
            value={descontoTexto}
            disabled={enviando}
            onChange={(evento) => {
              mexeu()
              setDescontoTexto(evento.target.value)
            }}
            placeholder="0,00 ou 10%"
            aria-invalid={desconto.erro || erroDesconto ? true : undefined}
            className="tabular text-right"
          />
          <span className="sr-only">Desconto em valor ou percentual</span>
        </label>
      </div>

      {desconto.centavos > 0 && (
        <div className="mt-2 flex justify-between text-[12.5px] text-atencao-texto">
          <span>
            Desconto aplicado
            {desconto.pontos !== undefined && ` (${formatar(desconto.pontos)}%)`}
          </span>
          <span className="tabular font-semibold">− {formatarMoeda(desconto.centavos)}</span>
        </div>
      )}

      {(desconto.erro || erroDesconto) && (
        <MensagemDeErro>{desconto.erro || erroDesconto}</MensagemDeErro>
      )}

      <div className="mt-3 flex items-baseline justify-between border-t border-divisoria pt-3">
        <span className="text-sm font-semibold text-tinta">Total</span>
        <span className="tabular text-[26px] font-bold text-tinta">
          {formatarMoeda(totalCentavos)}
        </span>
      </div>

      {/* ----------------------------------------------------- pagamento */}
      <p className="mt-4 mb-2 text-[12.5px] font-semibold text-texto">
        {dividido ? 'Primeira forma de pagamento' : 'Forma de pagamento'}
      </p>

      <SeletorDeForma
        rotulo={dividido ? 'Primeira forma de pagamento' : 'Forma de pagamento'}
        escolhida={formaPrimeira}
        desabilitado={enviando}
        razaoCrediario={razaoCrediario}
        idRazaoCrediario={idRazaoCrediario}
        aoEscolher={(forma) => {
          mexeu()
          setFormaPrimeira(forma)
          setParcelasPrimeira(1)
        }}
      />

      {razaoCrediario && (
        <p id={idRazaoCrediario} className="mt-2 text-[12px] leading-[1.5] text-texto-suave">
          {razaoCrediario}
        </p>
      )}

      <PainelDaForma
        forma={formaPrimeira}
        valorCentavos={valorPrimeiro}
        parcelas={parcelasPrimeira}
        parcelasMaximas={parcelasMaximas(formaPrimeira)}
        recebidoTexto={recebidoPrimeiroTexto}
        recebido={recebidoPrimeiro}
        cliente={cliente}
        desabilitado={enviando}
        sufixo="primeira"
        aoTrocarParcelas={(numero) => {
          mexeu()
          setParcelasPrimeira(numero)
        }}
        aoDigitarRecebido={(texto) => {
          mexeu()
          setRecebidoPrimeiroTexto(texto)
        }}
      />

      {/* ------------------------------------------------ segunda forma */}
      {!dividido ? (
        <button
          type="button"
          disabled={enviando || totalItens === 0}
          onClick={() => {
            mexeu()
            setDividido(true)
            // Metade por `ratear`, e não por divisão: 10,01 em duas formas dá
            // 5,01 e 5,00, e a soma continua exata (INV-01).
            setValorPrimeiroTexto(formatar(ratear(totalCentavos, 2)[0] ?? 0))
            setMostrarDivisao(true)
          }}
          className="mt-3 flex h-[34px] w-full items-center justify-center gap-2 rounded-sistema border border-borda bg-superficie text-[12.5px] font-semibold text-texto hover:border-inativo disabled:cursor-not-allowed disabled:text-inativo"
        >
          <Icone nome="add" tamanho={16} />
          Dividir em duas formas
        </button>
      ) : (
        <ResumoDaDivisao
          forma={formaSegunda}
          valorCentavos={valorSegundo}
          invalido={divisaoInvalida}
          aoEditar={() => setMostrarDivisao(true)}
          aoRemover={() => {
            mexeu()
            setDividido(false)
            setMostrarDivisao(false)
            setValorPrimeiroTexto('')
            setRecebidoSegundoTexto('')
          }}
        />
      )}

      {mostrarDivisao && (
        <Gaveta
          rotulo="Dividir pagamento"
          largura="formulario"
          aoFechar={() => setMostrarDivisao(false)}
        >
          <CabecalhoDaGaveta
            titulo="Dividir pagamento"
            subtitulo={`Total ${formatarMoeda(totalCentavos)}`}
            aoFechar={() => setMostrarDivisao(false)}
          />

          <CorpoDaGaveta>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2.5">
                <span className="flex-1 text-[13px] text-texto-suave">
                  Valor em {descricaoDaForma(formaPrimeira).rotulo}
                </span>
                <label
                  className="composto h-[34px] w-[140px] cursor-text"
                  aria-invalid={divisaoInvalida ? true : undefined}
                >
                  <span className="text-[13px] text-texto-suave" aria-hidden>
                    R$
                  </span>
                  <input
                    value={valorPrimeiroTexto}
                    disabled={enviando}
                    onChange={(evento) => {
                      mexeu()
                      setValorPrimeiroTexto(evento.target.value)
                    }}
                    placeholder="0,00"
                    aria-invalid={divisaoInvalida ? true : undefined}
                    className="tabular text-right font-semibold"
                  />
                  <span className="sr-only">
                    Valor na primeira forma ({descricaoDaForma(formaPrimeira).rotulo})
                  </span>
                </label>
              </div>

              <div>
                <p className="mb-2 text-[12.5px] font-semibold text-texto">
                  Segunda forma de pagamento
                </p>

                <SeletorDeForma
                  rotulo="Segunda forma de pagamento"
                  escolhida={formaSegunda}
                  desabilitado={enviando}
                  razaoCrediario={razaoCrediario}
                  idRazaoCrediario={idRazaoCrediario}
                  aoEscolher={(forma) => {
                    mexeu()
                    setFormaSegunda(forma)
                    setParcelasSegunda(1)
                  }}
                />
              </div>

              <div className="flex items-baseline justify-between text-[13px]">
                <span className="text-texto-suave">
                  Resto em {descricaoDaForma(formaSegunda).rotulo}
                </span>
                <span
                  className={`tabular font-bold ${divisaoInvalida ? 'text-erro' : 'text-tinta'}`}
                >
                  {formatarMoeda(valorSegundo)}
                </span>
              </div>

              {divisaoInvalida && (
                <MensagemDeErro>
                  O valor da primeira forma precisa ficar entre R$ 0,01 e{' '}
                  {formatarMoeda(Math.max(0, totalCentavos - 1))}.
                </MensagemDeErro>
              )}

              <PainelDaForma
                forma={formaSegunda}
                valorCentavos={Math.max(0, valorSegundo)}
                parcelas={parcelasSegunda}
                parcelasMaximas={parcelasMaximas(formaSegunda)}
                recebidoTexto={recebidoSegundoTexto}
                recebido={recebidoSegundo}
                cliente={cliente}
                desabilitado={enviando}
                sufixo="segunda"
                aoTrocarParcelas={(numero) => {
                  mexeu()
                  setParcelasSegunda(numero)
                }}
                aoDigitarRecebido={(texto) => {
                  mexeu()
                  setRecebidoSegundoTexto(texto)
                }}
              />
            </div>
          </CorpoDaGaveta>

          <RodapeDaGaveta>
            <button
              type="button"
              onClick={() => setMostrarDivisao(false)}
              className="flex h-[38px] items-center justify-center rounded-sistema bg-tinta px-4 text-[13px] font-bold text-white hover:bg-titulo"
            >
              Concluído
            </button>
          </RodapeDaGaveta>
        </Gaveta>
      )}

      {/*
        O erro do servidor aparece AQUI, colado na decisão que o causou —
        crediário recusado, soma que não fecha, parcela acima do limite do
        cliente —, e não num alerta genérico no alto da tela.
      */}
      {erroPagamento && (
        <div
          role="alert"
          className="mt-3 flex gap-2 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-erro-texto"
        >
          <Icone nome="error" tamanho={17} />
          <span>{erroPagamento}</span>
        </div>
      )}

      <button
        type="button"
        disabled={!pronto}
        onClick={finalizar}
        className={`mt-4 flex h-[46px] w-full items-center justify-center gap-2 rounded-sistema text-sm font-bold text-white ${
          pronto ? 'bg-tinta hover:bg-titulo' : 'cursor-not-allowed bg-inativo'
        }`}
      >
        <Icone nome={pronto ? 'check_circle' : 'lock'} tamanho={19} />
        {enviando ? rotuloAcaoOcupado : rotuloAcao}
      </button>

      <p role="status" className="mt-2.5 text-[12px] leading-[1.5] text-texto-fraco">
        {razaoBloqueio ||
          'A baixa de estoque e as parcelas do crediário são geradas pelo servidor, numa transação só.'}
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ peças */

function montarPagamento(
  forma: Forma,
  valorCentavos: Centavos,
  parcelas: number,
  recebido?: Centavos,
): PagamentoPedido {
  const pagamento: PagamentoPedido = { forma, valor_centavos: valorCentavos }

  // Parcela só viaja quando é mais de uma, e só na forma que parcela: o
  // servidor recusa `parcelas` em PIX (`check pagamento_parcela_so_a_prazo`).
  if (descricaoDaForma(forma).parcela && parcelas > 1) {
    pagamento.parcelas = parcelas
  }
  // Recebido só existe em dinheiro (`check pagamento_recebido_so_em_dinheiro`),
  // e só quando o operador informou o que o cliente entregou.
  if (forma === 'dinheiro' && recebido !== undefined && recebido >= valorCentavos) {
    pagamento.recebido_centavos = recebido
  }
  return pagamento
}

/**
 * O que fica na coluna quando a divisão já está configurada e a gaveta está
 * fechada — uma linha, não o formulário inteiro. Editar reabre a gaveta;
 * o valor por trás (`formaSegunda`/`valorCentavos`) já está gravado no estado
 * do `Fechamento`, então não há nada para "confirmar" ao fechar.
 */
function ResumoDaDivisao({
  forma,
  valorCentavos,
  invalido,
  aoEditar,
  aoRemover,
}: {
  forma: Forma
  valorCentavos: Centavos
  invalido: boolean
  aoEditar: () => void
  aoRemover: () => void
}) {
  return (
    <div
      className={`mt-3 flex items-center gap-2.5 rounded-sistema border p-3 ${
        invalido ? 'border-erro bg-erro-suave' : 'border-borda bg-superficie'
      }`}
    >
      <Icone nome={ICONE_FORMA[forma]} tamanho={18} className="text-tinta" />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold text-tinta">
          Dividida com {descricaoDaForma(forma).rotulo}
        </p>
        <p className={`tabular text-[12.5px] ${invalido ? 'text-erro' : 'text-texto-suave'}`}>
          {invalido ? 'Valor da divisão inválido' : formatarMoeda(Math.max(0, valorCentavos))}
        </p>
      </div>
      <button
        type="button"
        onClick={aoEditar}
        className="text-[12.5px] font-semibold text-marca hover:text-marca-forte"
      >
        Editar
      </button>
      <button
        type="button"
        onClick={aoRemover}
        className="text-[12.5px] font-semibold text-texto-suave hover:text-erro"
      >
        Remover
      </button>
    </div>
  )
}

function SeletorDeForma({
  rotulo,
  escolhida,
  desabilitado,
  razaoCrediario,
  idRazaoCrediario,
  aoEscolher,
}: {
  /** Nome do grupo — é o que distingue os dois seletores quando a conta é
   *  dividida, para leitor de tela e para teste. */
  rotulo: string
  escolhida: Forma
  desabilitado: boolean
  razaoCrediario: string
  idRazaoCrediario: string
  aoEscolher: (forma: Forma) => void
}) {
  return (
    <div role="group" aria-label={rotulo} className="grid grid-cols-3 gap-2">
      {FORMAS.map((item) => {
        const ativo = item.chave === escolhida
        // Crediário indisponível fica DESABILITADO, com a razão descrita ao
        // lado: impedir antes do clique vale mais que explicar depois dele.
        const bloqueada = item.chave === 'crediario' && razaoCrediario !== ''

        return (
          <button
            key={item.chave}
            type="button"
            aria-pressed={ativo}
            disabled={desabilitado || bloqueada}
            aria-describedby={bloqueada ? idRazaoCrediario : undefined}
            onClick={() => aoEscolher(item.chave)}
            className={`flex h-[46px] items-center justify-center gap-1.5 rounded-sistema border px-2 text-[12.5px] font-bold ${
              ativo
                ? 'border-marca bg-marca text-white'
                : bloqueada
                  ? 'cursor-not-allowed border-borda bg-superficie-suave text-inativo'
                  : 'border-borda bg-superficie text-tinta hover:border-inativo'
            }`}
          >
            <Icone nome={ICONE_FORMA[item.chave]} tamanho={17} />
            {item.rotulo}
          </button>
        )
      })}
    </div>
  )
}

/**
 * O painel que muda por forma — troco em dinheiro, parcela em crédito, limite
 * em crediário, e o aviso honesto nas duas formas de cartão/PIX.
 *
 * Nenhuma delas cobra de verdade: não há integração TEF nem cobrança PIX neste
 * sistema. O painel diz isso em vez de desenhar um QR que ninguém gera.
 */
function PainelDaForma({
  forma,
  valorCentavos,
  parcelas,
  parcelasMaximas,
  recebidoTexto,
  recebido,
  cliente,
  desabilitado,
  sufixo,
  aoTrocarParcelas,
  aoDigitarRecebido,
}: {
  forma: Forma
  valorCentavos: Centavos
  parcelas: number
  parcelasMaximas: number
  recebidoTexto: string
  recebido?: Centavos
  cliente: ClienteParaFechamento | null
  desabilitado: boolean
  /** Distingue os campos das duas formas quando a conta é dividida. */
  sufixo: string
  aoTrocarParcelas: (numero: number) => void
  aoDigitarRecebido: (texto: string) => void
}) {
  if (forma === 'dinheiro') {
    const informado = recebido !== undefined && recebido >= 0
    const troco = informado ? recebido - valorCentavos : 0
    const invalido = recebido === -1 || (informado && troco < 0)

    return (
      <div className="mt-3 rounded-sistema border border-borda p-3">
        <div className="flex items-center gap-2.5">
          <span className="flex-1 text-[13px] text-texto-suave">Valor recebido</span>
          <label
            className="composto h-[34px] w-[140px] cursor-text"
            aria-invalid={invalido ? true : undefined}
          >
            <span className="text-[13px] text-texto-suave" aria-hidden>
              R$
            </span>
            <input
              value={recebidoTexto}
              disabled={desabilitado}
              onChange={(evento) => aoDigitarRecebido(evento.target.value)}
              placeholder="0,00"
              aria-invalid={invalido ? true : undefined}
              className="tabular text-right font-semibold"
            />
            <span className="sr-only">Valor recebido em dinheiro ({sufixo} forma)</span>
          </label>
        </div>

        <div className="mt-2.5 flex justify-between border-t border-divisoria pt-2.5">
          <span className="text-[13px] font-semibold text-tinta">Troco</span>
          {!informado ? (
            <span className="tabular text-[15px] font-semibold text-texto-suave">—</span>
          ) : troco < 0 ? (
            <span role="alert" className="tabular text-[15px] font-bold text-erro">
              faltam {formatarMoeda(-troco)}
            </span>
          ) : (
            <span className="tabular text-[17px] font-bold text-ok">{formatarMoeda(troco)}</span>
          )}
        </div>

        {recebido === -1 && (
          <MensagemDeErro>Valor inválido. Escreva algo como 100,00.</MensagemDeErro>
        )}
      </div>
    )
  }

  if (forma === 'credito' || forma === 'crediario') {
    const opcoes = PARCELAS_OFERECIDAS.filter((numero) => numero <= parcelasMaximas)

    return (
      <div className="mt-3">
        <p className="mb-2 text-[12.5px] font-semibold text-texto">Parcelas</p>
        <div role="group" aria-label={`Parcelas (${sufixo} forma)`} className="flex flex-wrap gap-2">
          {opcoes.map((numero) => {
            const ativo = numero === parcelas
            return (
              <button
                key={numero}
                type="button"
                aria-pressed={ativo}
                disabled={desabilitado}
                onClick={() => aoTrocarParcelas(numero)}
                className={`tabular rounded-sistema border px-3 py-2 text-[12.5px] font-semibold ${
                  ativo
                    ? 'border-marca bg-marca-suave text-marca-forte'
                    : 'border-borda bg-superficie text-tinta hover:border-inativo'
                }`}
              >
                {numero}× de {formatarMoeda(valorDaParcela(valorCentavos, numero))}
              </button>
            )
          })}
        </div>

        {forma === 'crediario' && cliente && (
          <p className="mt-2.5 text-[12px] leading-[1.5] text-texto-suave">
            Limite de {formatarMoeda(cliente.crediario.limite_centavos)} em até{' '}
            {cliente.crediario.parcelas_max}×.{' '}
            {/*
              O quanto já está em aberto NÃO é sabido aqui — `fonte_saldo` do
              cliente é `aguarda_venda` enquanto não há histórico. Prometer um
              "disponível" que a tela não tem seria inventar número: quem
              confere o teto de dívida é o servidor, na hora de gravar.
            */}
            O saldo já usado é conferido pelo servidor ao finalizar.
          </p>
        )}

        {forma === 'credito' && (
          <p className="mt-2.5 text-[12px] leading-[1.5] text-texto-suave">
            Passe o cartão na maquininha e confirme a aprovação antes de finalizar — não há
            integração TEF neste sistema.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="mt-3 flex items-center gap-2.5 rounded-sistema border border-borda px-3 py-2.5">
      <Icone nome={ICONE_FORMA[forma]} tamanho={20} className="text-tinta" />
      <p className="text-[12px] leading-[1.5] text-texto-suave">
        {forma === 'pix'
          ? 'Confira o recebimento no aplicativo do banco antes de finalizar — não há cobrança PIX integrada.'
          : 'Passe o cartão na maquininha e confirme a aprovação antes de finalizar — não há integração TEF.'}
      </p>
    </div>
  )
}
